import React from "react";
import { cn } from "@/lib/utils";
import { Pencil } from "lucide-react";
import { renderCellValue } from "./cell-value-rendering";
import { EmailAliasesEditor } from "@/features/contacts/components/EmailAliasesEditor";
import { PhoneAliasesEditor } from "@/features/contacts/components/PhoneAliasesEditor";
import { TechnicalIdentifiersEditor } from "@/features/companies/components/TechnicalIdentifiersEditor";
import type { SimpleFieldConfig, FieldValue, BaseEntity } from "../types/index";

export interface EditableCellDisplayContainerProps<T extends BaseEntity = BaseEntity> {
  value: FieldValue;
  fieldConfig: SimpleFieldConfig;
  rowId: string;
  fieldId: string;
  canEdit: boolean;
  isTitleField?: boolean;
  customDisplayText?: string;
  data: T[];
  entityType?: string;
  onEdit: (rowId: string, fieldId: string, value: FieldValue) => void;
}

/**
 * Renders a cell in display mode (read-only)
 * Shows click-to-edit UI when editable, with hover effects and edit icon
 */
export function EditableCellDisplayContainer<T extends BaseEntity = BaseEntity>({
  value,
  fieldConfig,
  rowId,
  fieldId,
  canEdit,
  isTitleField = false,
  customDisplayText,
  data,
  entityType,
  onEdit,
}: EditableCellDisplayContainerProps<T>): React.ReactNode {
  const rowRecord = data.find((item) => item.id === rowId) as Record<string, unknown> | undefined;
  const renderOptions = {
    entityRecord: rowRecord,
    entityType,
  };
  return (
    <div className={cn("transition-all duration-200 ease-in-out", canEdit && "group relative")}>
      <div className="flex items-start gap-2 w-full">
        {fieldId === "email_aliases" && data.find((item) => item.id === rowId)?.email ? (
          // Special rendering for email_aliases field
          <EmailAliasesEditor
            contact={data.find((item) => item.id === rowId) as any}
            onUpdate={() => {
              // Update handled by component
            }}
          />
        ) : fieldId === "phone_aliases" && data.find((item) => item.id === rowId)?.id ? (
          <PhoneAliasesEditor
            contact={data.find((item) => item.id === rowId) as any}
            onUpdate={() => {
              // Update handled by component
            }}
          />
        ) : fieldId === "technical_identifiers" && data.find((item) => item.id === rowId)?.id ? (
          // Company support-engineering identifiers: bespoke structured editor
          // owns its own persistence (like the aliases editors above).
          <TechnicalIdentifiersEditor
            company={data.find((item) => item.id === rowId) as any}
          />
        ) : canEdit && !isTitleField ? (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(rowId, fieldId, value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onEdit(rowId, fieldId, value);
              }
            }}
            className={cn(
              "flex-1 min-w-0 break-words cursor-pointer transition-all duration-200",
              "hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:shadow-sm rounded px-2 py-1 -mx-2 -my-1",
              "hover:border hover:border-blue-200 dark:hover:border-blue-800",
              "group-hover:bg-blue-50 dark:group-hover:bg-blue-950/40 group-hover:shadow-sm",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            )}
            title="Click to edit"
          >
            {customDisplayText !== undefined
              ? customDisplayText
              : renderCellValue(value, fieldConfig, fieldId, false, renderOptions)}
          </div>
        ) : (
          <span className="flex-1 min-w-0 break-words">
            {customDisplayText !== undefined
              ? customDisplayText
              : renderCellValue(value, fieldConfig, fieldId, false, renderOptions)}
          </span>
        )}
        {/*
          Pencil is rendered ONLY for title fields. For non-title editable cells
          the entire value above is already a "Click to edit" control, so a
          separate pencil button would be a duplicate affordance (two adjacent
          controls firing the same onEdit). Title fields keep the pencil because
          their value is a plain, non-clickable span (the row click navigates).
          Regression: editable-cell-display-container.test.tsx
        */}
        {canEdit && isTitleField && (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(rowId, fieldId, value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onEdit(rowId, fieldId, value);
              }
            }}
            className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(pointer:coarse)]:opacity-100 transition-opacity flex-shrink-0 p-1 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded hover:shadow-sm cursor-pointer text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            title="Click to edit"
          >
            <Pencil className="h-3 w-3" />
          </div>
        )}
      </div>
    </div>
  );
}
